import { useNavigate, useParams } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  CostCentersListDocument,
  DeleteFinanceCostCenterDocument,
  FinanceCostCenterDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import WithPaywall from 'modules/finance/components/WithPaywall'
import { trackCostCenterAction } from 'modules/finance/lib/costCenterHelpers'

const CostCentersListPageDelete = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams()

  const { company } = useCurrentSession()

  const { data, loading } = useQuery(FinanceCostCenterDocument, {
    variables: {
      id: Number(id),
      companyId: company.id,
    },
  })

  const costCenter = data?.finance.costCentersConnection.nodes?.[0]

  const [deleteFinanceCostCenter, { loading: deleting }] = useMutation(
    DeleteFinanceCostCenterDocument,
    {
      extractorFn: (deleteData) => deleteData.financeMutations.deleteFinanceCostCenter,
    }
  )

  useUnsafeEffect(() => {
    if (costCenter) {
      trackCostCenterAction(trackingEvents.accounting.deleteCostCenterStart, costCenter)
    }
  }, [costCenter])

  const onClose = () => {
    navigate(navigation.settings.finance.costCenters.list.fullPath())
  }

  const handleClose = () => {
    trackCostCenterAction(trackingEvents.accounting.deleteCostCenterCancel)
    onClose()
  }

  const handleSubmit = async () => {
    const result = await deleteFinanceCostCenter({
      variables: { id: Number(id), companyId: company.id },
      refetchQueries: [CostCentersListDocument],
    })
    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('finance.modals.delete_cost_center.error') })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.modals.delete_cost_center.success'),
      })
      trackCostCenterAction(trackingEvents.accounting.deleteCostCenterSubmit, costCenter, 'success')
      navigate(navigation.settings.finance.costCenters.fullPath())
    }
  }

  return (
    <WithPaywall features={[Features.finance_cost_center_management]} fallback={<></>}>
      {() => (
        <Modal
          illustration="delete"
          onClose={handleClose}
          onSubmit={handleSubmit}
          title={i18n.t('finance.modals.delete_cost_center.header')}
          description={
            costCenter
              ? i18n.t('finance.modals.delete_cost_center.content', {
                  name: costCenter?.name,
                })
              : undefined
          }
          footer={
            <>
              <Button disabled={deleting} type="secondary" onClick={onClose}>
                {i18n.t('common.cancel')}
              </Button>
              <Button disabled={deleting || loading || !costCenter} submit>
                {i18n.t('finance.buttons.delete')}
              </Button>
            </>
          }
        >
          {loading && <Loading />}
        </Modal>
      )}
    </WithPaywall>
  )
}

export default CostCentersListPageDelete
